import get from 'lodash/get';
import map from 'lodash/map';

import { AUTH0_METADATA_CLAIM } from 'app/constants';
import { AuthServiceModule } from 'services/auth/auth.service';
import { AppSettingsModule } from 'services/settings.provider';
import { DataServiceModule } from 'services/data/data.service';
import { CreditCardServiceModule } from 'services/payment/stripe/creditCard.service';
import { SubscriptionServiceModule } from 'services/access/subscription/subscription.service';
import { CreditCardUtilitiesServiceModule } from 'services/utilities/creditCardUtilities.service';
import { CheckoutTrackerServiceModule } from 'services/tracking/enhancedEcommerce/checkoutTracker.service';
import { ProductsTrackerServiceModule } from 'services/tracking/enhancedEcommerce/productsTracker.service';
import { AuthTokenServiceModule } from 'services/auth/authToken.service';
import { StoreInformationServiceModule } from 'services/data/storeInformation.service';
import { trackEvent } from 'admin/services/tracking/GtmTracking';

export const CheckoutServiceModule = angular
    .module('app.components.checkout.checkoutService', [
        AuthServiceModule,
        AppSettingsModule,
        DataServiceModule,
        CreditCardServiceModule,
        SubscriptionServiceModule,
        CreditCardUtilitiesServiceModule,
        CheckoutTrackerServiceModule,
        ProductsTrackerServiceModule,
        AuthTokenServiceModule,
        StoreInformationServiceModule
    ]).service('checkoutService', class CheckoutService {
        constructor(
            $http,
            $q,
            $timeout,
            $window,
            authService,
            creditCardService,
            subscriptionsService,
            settings,
            dataService,
            authTokenService,
            creditCardUtilities,
            checkoutTracker,
            productsTracker,
            storeInformationService) {
            'ngInject';

            angular.extend(this, {
                $http,
                $q,
                $timeout,
                $window,
                authService,
                creditCardService,
                subscriptionsService,
                settings,
                dataService,
                authTokenService,
                creditCardUtilities,
                checkoutTracker,
                productsTracker,
                storeInformationService,
                cachedStoreInfo: null,
                STEP_ENTRANCE: 1,
                STEP_CONTACTINFO: 2,
                STEP_SHIPPINGADDRESS: 3,
                STEP_SHIPPINGMETHOD: 4,
                STEP_PAYMENTMETHOD: 5,
                STEP_BILLINGADDRESS: 6,
                SUBSCRIPTION_RETRY_DELAY: 1000
            });
        }

        getLoggedInUserDetails() {
            return this.$q.all([this.storeInformationService.getStore(), this.getPaymentMethod(), this.createUser()])
                .then(([storeInfo, paymentMethod, user]) => {
                    return { storeInfo, paymentMethod, user };
                });
        }

        getPaymentMethod() {
            return this.$http.get(`${process.env.BASE_API_URL}/admin/paymentmethod`).then(response => response.data);
        }

        getPlanDetails(planId) {
            return this.getPlans().then((plans) => {
                let planDetails;
                angular.forEach(plans, (plan) => {
                    if (planId === plan.id) planDetails = plan;
                });
                return planDetails;
            });
        }

        getPlans() {
            return this.$http.get(`${process.env.BASE_API_URL}/plans`).then(response => response.data);
        }

        getUser() {
            return this.authTokenService.getUser();
        }

        hasPaymentMethod(paymentMethod) {
            return angular.isDefined(paymentMethod)
                && angular.isDefined(paymentMethod.creditCard)
                && angular.isDefined(paymentMethod.creditCard.lastFour);
        }

        createUser() {
            return this.getUser().then((user) => {
                if (!user) return { isLoggedIn: false };

                if (user[AUTH0_METADATA_CLAIM]) {
                    user.user_metadata = user[AUTH0_METADATA_CLAIM];
                }

                return {
                    isLoggedIn: true,
                    contact: {
                        firstName: user.user_metadata
                            ? user.user_metadata.firstname
                            : user.given_name,
                        lastName: user.user_metadata
                            ? user.user_metadata.lastname
                            : user.family_name
                    },
                    newPayment: {},
                    user,
                    billingAddress: {
                        country: 'US'
                    },
                    couponCode: ''
                };
            }).catch(() => {
                return { isLoggedIn: false };
            });
        }

        setDetailsToUser(user, storeInfo, paymentMethod) {
            return angular.merge(user, {
                contact: {
                    phoneNumber: storeInfo.phoneNumber
                },
                account: {
                    email: storeInfo.email
                },
                forceEdit: !this.hasPaymentMethod(paymentMethod),
                payment: paymentMethod.creditCard,
                storeInfo
            });
        }

        mergeNewPaymentToPayment(user) {
            if (!user.payment) user.payment = {};
            return angular.merge(user.payment, {
                expirationString: user.newPayment.expiration,
                expiration: this.creditCardUtilities.parseExpiration(user.newPayment.expiration),
                lastFour: this.creditCardUtilities.getLastFour(user.newPayment.cardNumber),
                brand: this.creditCardUtilities.getCardType(user.newPayment.cardNumber),
                cardNumber: user.newPayment.cardNumber,
                cvv: user.newPayment.cvv
            });
        }

        placeOrder(user, cart, expireTrialInFiveMinutes, trialDaysRemaining, recaptchaResponse) {
            let couponCode;

            if (user.couponCode && user.couponCode !== '') {
                couponCode = user.couponCode;
                cart.couponCode = couponCode;
            }

            return this.getUser().then((serviceUser) => {
                if (serviceUser && !user.newPayment.cardNumber) {
                    return this.subscriptionsService.changePlan({
                        planId: cart.planId,
                        couponCode
                    });
                }

                this.mergeNewPaymentToPayment(user);

                let stripeCard = {
                    cardNumber: user.payment.cardNumber,
                    CVV: user.payment.cvv,
                    expiration: user.payment.expirationString
                };
                let stripeUser = { firstName: user.contact.firstName, lastName: user.contact.lastName };
                let stripeBillingAddress = user.billingAddress;
                let userToLog = serviceUser || user.account;

                return this.creditCardService.createStripeCcToken(stripeCard, stripeUser, stripeBillingAddress)
                    .then((cardResponse) => {
                        if (cardResponse.card && cardResponse.card.funding === 'prepaid') {
                            this.authTokenService.getTenantId().then((tenantID) => {
                                trackEvent(
                                    'prepaidCard',
                                    {
                                        type: 'purchase_attempt',
                                        tenantID
                                    }
                                );
                            });
                            throw new Error(`Prepaid cards are not currently accepted.
                                Please provide a credit or debit card, or contact us for help.`);
                        }
                        const stripeCcToken = get(cardResponse, 'id', null);
                        if (serviceUser) {
                            return this.changeUserPlan({
                                cart, stripeCcToken, couponCode, trialDaysRemaining
                            });
                        }
                        return this.registerNewUser({
                            user,
                            cart,
                            stripeCcToken,
                            expireTrialInFiveMinutes,
                            trialDaysRemaining,
                            recaptchaResponse
                        });
                    })
                    .catch((error) => {
                        this.$window.dataLayer.push({
                            event: 'paymentFailed'
                        });
                        return this.$q.reject(error);
                    })
                    .then(() => {
                        this.logSubscription(cart, userToLog);
                    });
            });
        }

        changeUserPlan({ cart, stripeCcToken, couponCode, trialDaysRemaining }) {
            return this.subscriptionsService.changePlan({
                planId: cart.planId,
                stripeToken: stripeCcToken,
                couponCode,
                trialDaysRemaining
            });
        }

        registerNewUser(params) {
            const navigator = this.$window.navigator;
            const registration = {
                firstName: params.user.contact.firstName,
                lastName: params.user.contact.lastName,
                phoneNumber: params.user.contact.phoneNumber,
                email: params.user.account.email,
                password: params.user.account.password,
                termsAndConditionsAccepted: true,
                planId: params.cart.planId,
                creditCardToken: params.stripeCcToken,
                expireTrialInFiveMinutes: params.expireTrialInFiveMinutes || false,
                userAgent: navigator.userAgent || navigator.vendor || navigator.platform || this.$window.opera,
                'g-recaptcha-response': params.recaptchaResponse
            };

            if (params.trialDaysRemaining) {
                registration.trialLengthInDays = params.trialDaysRemaining;
            }

            if (params.user.couponCode && params.user.couponCode !== '') {
                registration.couponCode = params.user.couponCode;
            }

            return this.authService.register(registration);
        }

        logSubscription(cart, userToLog) {
            this.subscriptionsService.getSubscription()
                .catch(this.getSubscriptionDelayedRetry.bind(this))
                .then((subscription) => {
                    const subscriptionInfo = {
                        id: subscription.id,
                        tenantId: subscription.tenant,
                        customerId: subscription.customer
                    };
                    this.logPurchase(cart, subscriptionInfo, userToLog);
                });
        }

        getSubscriptionDelayedRetry() {
            const deferred = this.$q.defer();
            this.$timeout(() => {
                this.subscriptionsService.getSubscription()
                    .then((subscription) => {
                        deferred.resolve(subscription);
                    })
                    .catch((error) => {
                        deferred.reject(error);
                    });
            }, this.SUBSCRIPTION_RETRY_DELAY);

            return deferred.promise;
        }

        generateProductfromPlan(plan) {
            return {
                title: plan.name,
                productCode: plan.id,
                price: plan.amount / 100,
                variant: plan.name.toLowerCase()
            };
        }

        logProductImpression(plans) {
            let products = map(plans, plan => this.generateProductfromPlan(plan));
            this.productsTracker.logProductImpression(products, 'plans', 'v2_inAdmin');
        }

        logProductClick(plan) {
            this.productsTracker.logProductClick(this.generateProductfromPlan(plan), 'plans', 'v2_inAdmin');
        }

        logProductDetails(plan) {
            this.productsTracker.logProductDetails(this.generateProductfromPlan(plan), 'plans', 'v2_inAdmin');
        }

        logAddToCart(plan) {
            this.checkoutTracker.logAddToCart('USD', this.generateProductfromPlan(plan), 'plans', 'v2_inAdmin');
        }

        logRemoveFromCart(plan) {
            this.checkoutTracker.logRemoveFromCart(this.generateProductfromPlan(plan), 'plans', 'v2_inAdmin');
        }

        logCheckoutStep(stepNumber, plan, optionalValue) {
            this.checkoutTracker.logCheckoutStep(
                this.generateProductfromPlan(plan), 'plans', stepNumber, optionalValue, 'v2_inAdmin');
        }

        logPurchase(cart, subscription, user, paidWith) {
            const plan = cart.planDetails;
            const productData = this.generateProductfromPlan(plan);
            this.checkoutTracker.logPurchase({
                orderNumber: subscription.id,
                affiliation: subscription.tenantId,
                product: productData,
                taxAmount: 0,
                grandTotal: plan.amount / 100,
                category: 'plans',
                paidWith: paidWith,
                couponCode: cart.couponCode
            });
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/components/checkout/checkout.service.js